import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Undo2, ChevronLeft, ChevronRight, History, Calculator, ToggleLeft, RefreshCw, Upload, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const ICONS = {
    bulk_margin: Calculator,
    bulk_disable: ToggleLeft,
    bulk_enable: ToggleLeft,
    sync_full: RefreshCw,
    sync_prices: RefreshCw,
    sync_stock: RefreshCw,
    manual_import: Upload,
    revert: RotateCcw,
};
const LABELS = {
    bulk_margin: "Margem em massa",
    bulk_disable: "Desactivação em massa",
    bulk_enable: "Activação em massa",
    sync_full: "Sync fornecedor (completo)",
    sync_prices: "Sync fornecedor (preços)",
    sync_stock: "Sync fornecedor (stocks)",
    manual_import: "Importação manual CSV",
    revert: "Reversão",
};

const PAGE = 25;

export default function AdminAudit() {
    const [data, setData] = useState({ items: [], total: 0 });
    const [page, setPage] = useState(1);
    const [busy, setBusy] = useState({});

    const load = async () => {
        const { data } = await api.get(`/admin/audit?limit=${PAGE}&skip=${(page - 1) * PAGE}`);
        setData(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

    const revert = async (id, summary) => {
        if (!window.confirm(`Reverter "${summary}"? A acção será registada.`)) return;
        setBusy((s) => ({ ...s, [id]: true }));
        try {
            const { data } = await api.post(`/admin/audit/${id}/revert`);
            toast.success(`${data.modified} produtos revertidos ✓`, { duration: 6000 });
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro a reverter");
        } finally { setBusy((s) => ({ ...s, [id]: false })); }
    };

    const totalPages = Math.max(1, Math.ceil(data.total / PAGE));

    return (
        <div data-testid="admin-audit-page">
            <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display font-black text-4xl tracking-tighter">Histórico</h1>
                    <p className="text-sm text-ink/60 mt-1">{data.total} alterações em massa registadas. Reverte com 1 clique.</p>
                </div>
            </div>

            <div className="border-2 border-ink bg-white overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-ink text-white text-xs font-mono uppercase tracking-wider">
                        <tr>
                            <th className="text-left p-3">Acção</th>
                            <th className="text-left p-3">Resumo</th>
                            <th className="text-left p-3">Utilizador</th>
                            <th className="text-right p-3">Afectados</th>
                            <th className="text-left p-3">Data</th>
                            <th className="text-right p-3">Acções</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.items.map((it) => {
                            const Icon = ICONS[it.action] || History;
                            const label = LABELS[it.action] || it.action;
                            return (
                                <tr key={it.id} className="border-t-2 border-ink/10 hover:bg-magenta/5" data-testid={`audit-row-${it.id}`}>
                                    <td className="p-3">
                                        <span className="inline-flex items-center gap-1.5 text-xs font-mono">
                                            <Icon size={14} className={it.reverted ? "text-ink/40" : "text-magenta"} />
                                            {label}
                                        </span>
                                    </td>
                                    <td className="p-3 max-w-md">
                                        <div className={it.reverted ? "line-through text-ink/40" : ""}>{it.summary || "—"}</div>
                                        {it.filters && Object.values(it.filters).some(Boolean) && (
                                            <div className="text-[11px] font-mono text-ink/50 mt-0.5">
                                                {Object.entries(it.filters).filter(([, v]) => v).map(([k, v]) => `${k}=${v}`).join(" · ")}
                                            </div>
                                        )}
                                    </td>
                                    <td className="p-3 text-xs font-mono">{it.admin_email}</td>
                                    <td className="p-3 text-right font-mono font-bold">{it.affected}</td>
                                    <td className="p-3 text-xs font-mono">
                                        {new Date(it.created_at).toLocaleString("pt-PT")}
                                    </td>
                                    <td className="p-3 text-right">
                                        {it.can_revert && !it.reverted && (
                                            <button
                                                onClick={() => revert(it.id, it.summary || it.action)}
                                                disabled={busy[it.id]}
                                                className="text-xs font-bold uppercase border-2 border-ink px-3 py-1.5 hover:bg-magenta hover:text-white hover:border-magenta disabled:opacity-50 inline-flex items-center gap-1"
                                                data-testid={`revert-${it.id}`}
                                            >
                                                <Undo2 size={12} /> Reverter
                                            </button>
                                        )}
                                        {it.reverted && (
                                            <span className="text-[10px] font-mono bg-ink/10 px-2 py-1 uppercase tracking-wider">REVERTIDO</span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {data.items.length === 0 && (
                            <tr>
                                <td colSpan={6} className="p-12 text-center text-ink/50 font-mono text-sm">
                                    Nenhuma alteração em massa registada ainda.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
                    <div className="font-mono text-xs text-ink/60">
                        Página {page} de {totalPages} · {data.total} registos
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="btn-secondary disabled:opacity-40" data-testid="audit-prev">
                            <ChevronLeft size={14} /> Anterior
                        </button>
                        <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="btn-secondary disabled:opacity-40" data-testid="audit-next">
                            Seguinte <ChevronRight size={14} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
